/**
 * Which browser this is, so a second sign-in here replaces the first.
 *
 * <h2>What problem this solves</h2>
 *
 * Every sign-in writes a row to `oauth2_authorization` and nothing takes the old
 * one away, so signing in twice in the same browser leaves the first session
 * behind with a refresh token valid for thirty days that nobody will ever
 * deliberately use. The backend already replaces it — `DeviceSessionStore`
 * deletes this user's rows on this device before saving the new one — but that
 * statement is a no-op without a device to scope it to, so `device_id` was NULL
 * on every row this application ever created and nothing was ever replaced.
 *
 * <p>The value here is what fills it. One identifier per browser profile, sent on
 * the token request, and the same one every time.
 *
 * <h2>Why not the cookie the backend already writes</h2>
 *
 * `DeviceCookieFilter` mints one and `TokenResponseCookieFilter` writes it — but
 * only for a caller that sent `X-Auth-Cookie: true`, which is the request to put
 * the *tokens* in HttpOnly cookies as well. That is the intended end state (see
 * {@link tokenStorage}) and it is not this change: asking for cookie mode to get
 * a device id would switch the whole session mechanism as a side effect.
 *
 * <p>`X-Device-ID` is the backend's documented opt-in for exactly this case — a
 * caller that manages its own identifier — and it never causes a cookie to be
 * written.
 *
 * <h2>It must outlive a sign-out, and does</h2>
 *
 * `tokenStorage.clear()` removes the two token keys and this is a third, so
 * signing out forgets the session and keeps the browser's identity. That is the
 * point: the next sign-in here has to be recognisable as the same device, or it
 * accumulates a row instead of replacing one.
 *
 * <h2>An id that cannot be stored is not sent</h2>
 *
 * `localStorage` throws in a private window with site data blocked, and in some
 * embedded webviews. A value minted and not persisted would be different on the
 * next sign-in, so it could never match anything — it would fill `device_id` with
 * single-use values and make the table read as a hundred devices where there is
 * one. The backend's own comment says NULL is the honest answer for a caller with
 * no stable device, so this returns `null` and the header is omitted. The cost is
 * the behaviour that browser has today.
 *
 * <h2>It is an identifier and not a credential</h2>
 *
 * It proves nothing and is not checked against anything. Knowing somebody's
 * device id lets you say "I am that browser" and nothing else: the delete is
 * scoped to the *authenticated* principal as well, so it can only ever remove
 * sessions belonging to whoever just proved who they are. There is nothing here
 * worth stealing, which is why a random value is enough and why no attempt is
 * made to fingerprint the machine — a fingerprint identifies the person across
 * sign-outs whether they want it or not, and is both more invasive and less
 * stable than a value we mint once.
 */

const DEVICE_ID = 'xtremand.device_id';

/**
 * 128 random bits as hex.
 *
 * <p>`getRandomValues` rather than `randomUUID`, which is restricted to a secure
 * context and therefore throws when the dev server is reached over plain HTTP by
 * IP — a sign-in that fails on a colleague's machine and nowhere else. This needs
 * no secure context and the value is not a UUID anybody parses.
 */
function mintDeviceId(): string {
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
}

/**
 * This browser's device id, minting and storing one on first use.
 *
 * <p>Not memoised. It is read once or twice per sign-in, a storage read is cheap,
 * and a cached value would have to be invalidated by anything that cleared
 * storage underneath it.
 *
 * @returns the id, or `null` when storage is unavailable — see the note above on
 *   why an unstorable id is not sent
 */
export function deviceId(): string | null {
  try {
    const existing = window.localStorage.getItem(DEVICE_ID);
    if (existing !== null && existing.length > 0) {
      return existing;
    }

    const minted = mintDeviceId();
    window.localStorage.setItem(DEVICE_ID, minted);

    // Read back rather than trusting the write. Safari's private mode accepts
    // setItem and keeps nothing, so the only way to know the id will still be
    // here on the next sign-in is to find it there now.
    return window.localStorage.getItem(DEVICE_ID);
  } catch {
    return null;
  }
}
